import { z } from 'zod';
import { LaborUnit, LaborCategory, ComplexityTier, FinishFaceFilter, LaborPricingMode } from '../enums';

const laborUnitValues = Object.values(LaborUnit) as [string, ...string[]];
const laborCategoryValues = Object.values(LaborCategory) as [string, ...string[]];
const complexityTierValues = Object.values(ComplexityTier) as [string, ...string[]];
const finishFaceFilterValues = Object.values(FinishFaceFilter) as [string, ...string[]];
const laborPricingModeValues = Object.values(LaborPricingMode) as [string, ...string[]];

export const LaborRateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  category: z.string(),
  rate_per_unit: z.number(),
  unit: z.string(),
  pricing_mode: z.enum(laborPricingModeValues).default('fixed'),
  hourly_rate: z.number().nullable().optional(),
  auto_apply: z.boolean().default(true),
  complexity: z.string().default('any'),
  cabinet_type_filter: z.string().nullable(),
  min_width: z.number().nullable(),
  max_width: z.number().nullable(),
  min_panel_count: z.number().int().nullable(),
  max_panel_count: z.number().int().nullable(),
  min_zone_count: z.number().int().nullable(),
  has_joints: z.boolean().nullable(),
  has_cubby_grid: z.boolean().nullable(),
  has_merged_cells: z.boolean().nullable(),
  min_cubby_cells: z.number().int().nullable(),
  min_sub_zones: z.number().int().nullable(),
  min_leaf_zones: z.number().int().nullable(),
  template_name_filter: z.string().nullable(),
  finish_face_filter: z.string().default('all'),
  min_complexity_score: z.number().int().nullable(),
  max_complexity_score: z.number().int().nullable(),
  product_type_filter: z.string().nullable(),
  min_section_depth: z.number().nullable(),
  max_section_depth: z.number().nullable(),
  min_section_height: z.number().nullable(),
  max_section_height: z.number().nullable(),
  min_section_width: z.number().nullable(),
  max_section_width: z.number().nullable(),
  min_rib_count: z.number().int().nullable(),
  min_rail_count: z.number().int().nullable(),
  min_slot_count: z.number().int().nullable(),
  material_type_filter: z.string().nullable(),
  requires_finish_filter: z.boolean().nullable(),
  is_cladded_filter: z.boolean().nullable(),
  notes: z.string().nullable(),
  active: z.boolean().default(true),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
}).superRefine((val, ctx) => {
  if (val.pricing_mode === 'per_hour' && (val.hourly_rate == null || val.hourly_rate <= 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'hourly_rate is required and must be > 0 when pricing_mode is per_hour',
      path: ['hourly_rate'],
    });
  }
});

/** Refinement: when pricing_mode = per_hour, hourly_rate must be a positive number. */
const perHourRefinement = (
  val: { pricing_mode?: string; hourly_rate?: number | null },
  ctx: z.RefinementCtx,
) => {
  if (val.pricing_mode === 'per_hour' && (val.hourly_rate == null || val.hourly_rate <= 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'hourly_rate is required and must be > 0 when pricing_mode is per_hour',
      path: ['hourly_rate'],
    });
  }
};

const _CreateLaborRateBase = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.enum(laborCategoryValues, { errorMap: () => ({ message: 'Invalid category' }) }),
  rate_per_unit: z.number(),
  unit: z.enum(laborUnitValues, { errorMap: () => ({ message: 'Invalid unit' }) }),
  pricing_mode: z.enum(laborPricingModeValues).default('fixed'),
  hourly_rate: z.number().positive().nullable().optional(),
  auto_apply: z.boolean().default(true),
  complexity: z.enum(complexityTierValues).default('any'),
  cabinet_type_filter: z.string().nullable().optional(),
  min_width: z.number().nullable().optional(),
  max_width: z.number().nullable().optional(),
  min_panel_count: z.number().int().nullable().optional(),
  max_panel_count: z.number().int().nullable().optional(),
  min_zone_count: z.number().int().nullable().optional(),
  has_joints: z.boolean().nullable().optional(),
  has_cubby_grid: z.boolean().nullable().optional(),
  has_merged_cells: z.boolean().nullable().optional(),
  min_cubby_cells: z.number().int().nullable().optional(),
  min_sub_zones: z.number().int().nullable().optional(),
  min_leaf_zones: z.number().int().nullable().optional(),
  template_name_filter: z.string().nullable().optional(),
  finish_face_filter: z.enum(finishFaceFilterValues).default('all'),
  min_complexity_score: z.number().int().nullable().optional(),
  max_complexity_score: z.number().int().nullable().optional(),
  product_type_filter: z.string().nullable().optional(),
  min_section_depth: z.number().nullable().optional(),
  max_section_depth: z.number().nullable().optional(),
  min_section_height: z.number().nullable().optional(),
  max_section_height: z.number().nullable().optional(),
  min_section_width: z.number().nullable().optional(),
  max_section_width: z.number().nullable().optional(),
  min_rib_count: z.number().int().nullable().optional(),
  min_rail_count: z.number().int().nullable().optional(),
  min_slot_count: z.number().int().nullable().optional(),
  material_type_filter: z.string().nullable().optional(),
  requires_finish_filter: z.boolean().nullable().optional(),
  is_cladded_filter: z.boolean().nullable().optional(),
  notes: z.string().nullable().optional(),
  active: z.boolean().default(true),
});

export const CreateLaborRateSchema = _CreateLaborRateBase.superRefine(perHourRefinement);
export const UpdateLaborRateSchema = _CreateLaborRateBase.partial().superRefine(perHourRefinement);

export type LaborRate = z.infer<typeof LaborRateSchema>;
export type CreateLaborRate = z.infer<typeof CreateLaborRateSchema>;
export type UpdateLaborRate = z.infer<typeof UpdateLaborRateSchema>;

// ── Labor Rate Tester Types ──

export const LaborRateTestInputSchema = z.object({
  frame_config: z.string().nullable().optional(),
  width: z.number().default(0),
  height: z.number().default(0),
  depth: z.number().default(0),
  num_doors: z.number().int().default(0),
  num_drawers: z.number().int().default(0),
  num_shelves: z.number().int().default(0),
  num_hinges: z.number().int().default(0),
  num_pulls_knobs: z.number().int().default(0),
  cabinet_type: z.string().default(''),
  template_name: z.string().default(''),
  has_finish: z.boolean().default(false),
  finish_sqft_all: z.number().default(0),
  finish_sqft_doors: z.number().default(0),
  finish_sqft_exterior: z.number().default(0),
  product_type: z.string().nullable().optional(),

  /** Pillow design key, for exercising a design_type_filter from the rate
   *  tester. Optional: the cabinet and banquette test paths ignore it. */
  design_type: z.string().optional(),
  lf_quantity: z.number().default(0),
  section_count: z.number().int().default(0),
  section_depth: z.number().default(0),
  section_height: z.number().default(0),
  rib_count: z.number().int().default(0),
  rail_count: z.number().int().default(0),
  slot_count: z.number().int().default(0),
  primary_material_type: z.string().default(''),
  requires_finish: z.boolean().default(false),
  is_cladded: z.boolean().default(false),
});

export type LaborRateTestInput = z.infer<typeof LaborRateTestInputSchema>;

export interface LaborRateTestMatchedRate {
  rate_id: string;
  rate_name: string;
  category: string;
  unit: string;
  rate_per_unit: number;
  unit_count: number;
  cost: number;
}

export interface LaborRateTestUnmatchedRate {
  rate_id: string;
  rate_name: string;
  category: string;
  reason: string;
}

export interface LaborRateTestAnalysis {
  complexity: string;
  complexity_score: number;
  panel_count: number;
  zone_count: number;
  has_joints: boolean;
  has_cubby_grid: boolean;
  cubby_cell_count: number;
  cubby_merge_count: number;
  sub_zone_count: number;
  leaf_zone_count: number;
  width: number;
  height: number;
  depth: number;
  num_doors: number;
  num_drawers: number;
  num_shelves: number;
  num_hinges: number;
  num_pulls_knobs: number;
  has_finish: boolean;
  is_frame_builder: boolean;
  product_type?: string;
  lf_quantity?: number;
  section_count?: number;
  section_depth?: number;
  section_height?: number;
  section_width?: number;
  rib_count?: number;
  rail_count?: number;
  slot_count?: number;
  panel_count_per_section?: number;
  primary_material_type?: string;
  requires_finish?: boolean;
  is_cladded?: boolean;
}

export interface LaborRateTestResult {
  analysis: LaborRateTestAnalysis;
  matched_rates: LaborRateTestMatchedRate[];
  unmatched_rates: LaborRateTestUnmatchedRate[];
  manual_rate_count: number;
  total_cost: number;
}
